<template>
  <nav
    class="navbar navbar-expand-xl navbar-light fixed-top bg-light border-0 shadow-sm"
  >
    <div class="container-fluid">
      <div class="d-flex align-items-center">
        <ProductSuiteLauncher class="me-2" data-test="productSuiteLauncher" />
        <router-link
          :to="{ name: product.homeRouteName }"
          class="navbar-brand brand-slot"
          data-test="navbarBrand"
        >
          <!-- The RepSignal PNG is padded: at 43px its artwork is 25.6px tall.
               The SVG has no padding, so match the artwork height directly. -->
          <img
            v-if="isMarketSignal"
            src="@/assets/MarketSignalLogo.svg"
            class="market-signal-logo"
            alt="MarketSignal"
            data-test="marketSignalLogo"
          />
          <img
            v-else
            src="@/assets/RepSignalLogo.png"
            height="43"
            alt=""
            data-test="repSignalLogo"
          />
        </router-link>
        <ProcedureModuleSwitcher
          v-if="!isMarketSignal && procMods.length != 0"
          :procMods="procMods"
          @module-switch="(procModId) => $emit('module-switch', procModId)"
        />
      </div>

      <UniversalSearchBar
        style="max-width: 580px"
        v-if="!isMobileDevice && !isMarketSignal"
      />
      <MarketSignalSearchBar
        style="max-width: 580px"
        v-if="!isMobileDevice && isMarketSignal"
      />
      <button
        class="navbar-toggler ms-2"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarCollapse"
        aria-controls="navbarCollapse"
        aria-expanded="false"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>
      <div class="collapse navbar-collapse" id="navbarCollapse">
        <ul class="navbar-nav mb-2 mb-lg-0">
          <div v-if="isMarketSignal" class="navbar-nav">
            <li class="nav-item">
              <router-link
                :to="{ name: 'marketSignalHome' }"
                class="nav-link"
                data-test="marketSignalHomeLink"
              >
                Home
              </router-link>
            </li>
          </div>
          <div v-else class="navbar-nav">
            <li class="nav-item">
              <router-link :to="{ name: 'home' }" class="nav-link">
                Home
              </router-link>
            </li>
            <li class="nav-item">
              <router-link :to="{ name: 'explorer' }" class="nav-link">
                Explorer
              </router-link>
            </li>
            <li v-if="isLeader" class="nav-item">
              <router-link :to="{ name: 'region_manager' }" class="nav-link">
                Territories
              </router-link>
            </li>
            <li v-if="isLeader && hasMarketSummaryAccess" class="nav-item">
              <router-link
                :to="{ name: 'marketSummary' }"
                class="nav-link d-flex flex-nowrap align-items-center"
              >
                Markets
                <span class="badge beta-badge ms-1">BETA</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link :to="{ name: 'lists' }" class="nav-link">
                Lists
              </router-link>
            </li>
            <li class="nav-item">
              <router-link
                :to="{ name: 'newsfeed' }"
                class="nav-link d-flex flex-nowrap"
              >
                News
                <span v-if="news.showIndicator"
                  ><i
                    class="pi pi-circle-on align-text-top"
                    style="font-size: 0.55rem; color: var(--S2NPacificTeal)"
                  ></i>
                </span>
              </router-link>
            </li>
          </div>

          <li v-if="!isMarketSignal" class="nav-item">
            <BaseModalButton
              class="btn nav-link position-relative notes-button"
              title="Intel Book"
              modalId="notesModal"
            >
              <i class="pi pi-book text-black" />
              <span
                v-show="unreadNotes.length !== 0"
                class="notification position-absolute top-25 start-75 translate-middle border border-light rounded-circle bg-info"
                title="New notes have been shared with you"
              >
                <span class="visually-hidden">new notes</span>
              </span>
            </BaseModalButton>
          </li>

          <li class="nav-item animate">
            <a
              class="nav-link"
              :href="GENERAL_REPSIGNAL_HELP_LINK"
              target="_blank"
              ><i class="pi pi-question-circle"></i
            ></a>
          </li>
          <li class="nav-item animate nav-li dropdown">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              data-cy="NavCogDropdown"
            >
              <i class="pi pi-cog"></i>
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <router-link
                  :to="{ name: 'account' }"
                  class="nav-link text-dark"
                  >My Account</router-link
                >
              </li>
              <li class="nav-item" v-if="user.is_admin">
                <a class="nav-link" :href="getEnvUrl + '/admin/login'">
                  Admin
                </a>
              </li>
              <li>
                <router-link
                  :to="{ name: 'logout' }"
                  class="nav-link text-dark"
                  data-cy="NavLogoutButton"
                  >Logout</router-link
                >
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
    <UniversalSearchBar
      class="px-3 py-1"
      v-if="isMobileDevice && !isMarketSignal"
    />
    <MarketSignalSearchBar
      class="px-3 py-1"
      v-if="isMobileDevice && isMarketSignal"
    />
  </nav>
</template>

<script>
import { useLoggedInUserStore } from "@/store/loggedInUser";
import { useNewsStore } from "@/store/news";
import { useProductStore } from "@/store/product";
import ProcedureModuleSwitcher from "./ProcedureModuleSwitcher.vue";
import ProductSuiteLauncher from "./ProductSuiteLauncher.vue";
import BaseModalButton from "@/components/buttons/BaseModalButton.vue";
import isMobile from "@/composables/datatable/isMobile";
import { GENERAL_REPSIGNAL_HELP_LINK } from "@/services/infoTooltips";
import UniversalSearchBar from "@/components/ui/UniversalSearch/UniversalSearchBar.vue";
import MarketSignalSearchBar from "@/components/ui/MarketSignalSearchBar.vue";

export default {
  components: {
    ProcedureModuleSwitcher,
    ProductSuiteLauncher,
    BaseModalButton,
    UniversalSearchBar,
    MarketSignalSearchBar,
  },
  props: {
    procMods: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["module-switch"],
  setup() {
    const user = useLoggedInUserStore();
    const news = useNewsStore();
    const product = useProductStore();
    const { isMobileDevice } = isMobile("lg");
    return {
      user,
      news,
      product,
      isMobileDevice,
      GENERAL_REPSIGNAL_HELP_LINK,
    };
  },
  data() {
    return {
      hasMarketSummaryAccess: false,
    };
  },
  computed: {
    getEnvUrl() {
      return import.meta.env.VITE_FLASK_URL;
    },
    unreadNotes() {
      return this.user.notes.filter((note) => !note.has_been_read);
    },
    isLeader() {
      return this.user.isLeader;
    },
    isMarketSignal() {
      return this.product.isMarketSignal;
    },
  },
  async created() {
    this.hasMarketSummaryAccess = await this.user.getFeatureFlag(
      "marketSummary"
    );
  },
};
</script>

<style scoped>
a {
  color: black !important;
}
.notification {
  padding: 0.4em;
}
.router-link-exact-active {
  font-weight: 600;
}
.notes-button {
  border: none;
  color: black;
  transition: color 0.15s;
}
.notes-button:focus {
  box-shadow: none;
}
.notes-button i {
  transition: color 0.15s;
}
.notes-button:hover i {
  color: #124e70 !important;
}
.ocean-blue-button-inverse {
  --bs-btn-font-size: 0.875rem;
}

.navbar-collapse {
  flex-grow: 0;
}
/* Keep the brand slot the same height in both products so the navbar
   height (and anything measured against it) does not shift. The RepSignal
   image is 43px tall inside Bootstrap's .navbar-brand vertical padding
   (0.3125rem each side); min-height is border-box, so include that padding. */
.brand-slot {
  display: flex;
  align-items: center;
  min-height: calc(43px + 2 * 0.3125rem);
}
.market-signal-logo {
  height: 25.6px;
  width: auto;
}
</style>
